import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../utils/firebase";
import { maybeForceReloadForMinVersion } from "../utils/versionGate";
import { maybeRefetchForStaleConfig } from "../utils/configVersionGate";

/**
 * Subscribes to the `app_config/maintenance` Firestore doc and returns
 * `{ active, message }`. `active` is true only when the doc exists and
 * `active === true`; `message` is the optional custom copy from the doc
 * (undefined when unset, so the banner falls back to default copy).
 * Read failures (offline, permissions) default to `active: false` so
 * the banner fails closed. The error is logged via `console.warn` so a
 * future rule misconfig (e.g. tightened to authed-only by mistake) is
 * visible in the browser console rather than silently swallowed.
 *
 * This is the app's only persistent Firestore listener, so it also carries two
 * push-based freshness signals off the same doc — each a no-op unless its field
 * is set:
 *   - `minSupportedVersion` → evicts a live client whose running build is below
 *     the gate via a hard reload (a seconds-latency kill switch for a bad
 *     build). See `versionGate`.
 *   - `configVersion` → silently re-fetches `userData` when the served
 *     workspace-config token is stale, so a long-open tab picks up a config
 *     change without a reload. See `configVersionGate`.
 */
export function useMaintenanceFlag() {
  const [state, setState] = useState({ active: false, message: undefined });

  useEffect(() => {
    const ref = doc(db, "app_config", "maintenance");
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : undefined;
        maybeForceReloadForMinVersion(
          import.meta.env.VITE_APP_VERSION,
          data?.minSupportedVersion,
        );
        maybeRefetchForStaleConfig(data?.configVersion);
        if (data?.active) {
          setState({ active: true, message: data?.message });
        } else {
          setState({ active: false, message: undefined });
        }
      },
      (err) => {
        console.warn("[useMaintenanceFlag] snapshot error", err);
        setState({ active: false, message: undefined });
      },
    );
    return unsubscribe;
  }, []);

  return state;
}
